/* COPY THIS PAGE FOR A SITE BUILDER.
 *
 * The whole Sites integration, in one button — and since 2026-10-27 that one
 * button asks which builder (COPY_TARGETS). GoHighLevel's API cannot create
 * funnel pages, so there is nothing to push — what their AI builder takes is a
 * JSON brief, and the schema is deliberately builder-agnostic
 * (docs/site-brief-spec.md). So this is not a GHL feature. It is her page, in a
 * shape any builder or model can build from, on the clipboard.
 *
 * WHY THE WARNINGS ARE ON SCREEN AND NOT BURIED. Handed a brief with a hole in
 * it, a builder fills the hole — the first one invented "Marcus Reyes, CEO,
 * Northwind Logistics" and a 4.9/5 rating. So what the brief could NOT say is
 * shown before she pastes, not after someone asks why their site has a
 * testimonial from a person who does not exist.
 */
import { useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { ChevronDown, ClipboardCopy, Eye } from "lucide-react";
import { brandSkin } from "../../utils/brand";
import { siteBrief, sitePrompt } from "../../utils/siteBrief";
import { ghlPagePrompt } from "../../utils/ghlPagePrompt";
import { SitePreview } from "./sitePreview";
import { Menu2 } from "./ui";
import { B, btnGhost } from "./tokens";

/* WHERE THE PAGE CAN GO. ONE button, then the target (her call, 2026-10-27:
   "can there just be one copy button that then outputs to different specs?").
   Each is the same page in the shape that builder reads; nothing calls Brill
   and nothing connects anywhere. `admin` targets are admin-only while new.
   The written prompt is every client's (2026-10-27, her call: "GHL isn't
   connectable but Wix is just a prompt").
   A new builder is a row here and a function beside siteBrief, never another
   button. */
export const COPY_TARGETS = [
  /* FIRST because it is what most of them will want: words, which any AI
     that builds a site reads — Wix's, Claude, whatever they use. Named for
     what it IS, not a builder (her call: "it doesn't need to say wix unless
     it's really specific"). */
  { id: "prompt", ico: "✍️", name: "Written prompt", hint: "Your words, colors and pictures, for any AI that builds sites.", admin: false,
    done: "Copied. Paste it into the AI that's building your site." },
  { id: "any", ico: "🧩", name: "A structured spec (JSON)", hint: "The same page as data, for a builder or AI that reads JSON.", admin: false,
    done: "Copied. Paste it into your site builder." },
  { id: "ghl", ico: "⚡", name: "GoHighLevel's AI builder", hint: "A written prompt — GHL's builder takes words, not a spec.", admin: true,
    done: "Copied. In GHL, open Sites → AI builder and paste it into a new project." },
];

/**
 * @param page      the page being looked at
 * @param sections  its sections, in order
 * @param outputs   her locked Accelerator sections, by module — App's mergedOutputs
 * @param identity  { businessName, ownerName, ... } as far as anything holds it
 * @param ghl       also offer GHL as a target, and the PREVIEW of the
 *                  page (admin-only while new)
 * @param worthi    her WORTHI category, read from My Offer (utils/worthi)
 */
export function CopySiteBrief({ page, sections = [], outputs = {}, identity = {}, ghl = false, worthi = null }) {
  /* ONE read-out, for whichever target she last copied — so a note never
     shows under the wrong builder. Above the early return, like the hooks
     below. */
  const [state, setState] = useState(null);   // { target, ok, notes, missing, error }
  const [menuOpen, setMenuOpen] = useState(false);
  /* The preview is the first look before any copy — so it sits first. */
  const [previewOpen, setPreviewOpen] = useState(false);
  /* HER NAME IS ALREADY KNOWN — she signed in with it. Asking for it again in
     settings, and reporting it "missing" until she does, is Brill forgetting
     something it was told. Settings still wins where she has set a display
     name, because that is the name she chose to be called.
     Both hooks above the early return: a component that runs a different
     number of them depending on its props is React #310 (src/hookOrder.test.js). */
  const { user } = useUser();
  const fromAccount = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();

  const written = sections.filter((s) => Object.values(s?.content || {}).some((v) => (Array.isArray(v) ? v.length : String(v || "").trim())));
  if (!page?.id || !written.length) return null;

  const targets = COPY_TARGETS.filter((t) => ghl || !t.admin);

  /* The page in the chosen builder's shape. One reader of her locked prose,
     shared with the decks — see the note at the top of brandSkin.js about
     that seam failing silently. */
  const build = (target) => {
    const { skin } = brandSkin({ brand: identity.brand, voiceOutputs: outputs.voice || {} });
    const who = { ...identity, ownerName: identity.ownerName || fromAccount };
    const fonts = { heading: skin.fontHeading, body: skin.fontBody };
    if (target === "ghl") {
      /* GHL'S AI BUILDER takes a written prompt, not JSON (proven by hand
         2026-10-16, docs/prototypes/ghl-prompt.md). */
      const { prompt, notes } = ghlPagePrompt({ page, sections, skin, worthi, identity: who });
      return { text: prompt, notes, missing: [] };
    }
    if (target === "prompt") {
      const { prompt, notes, missing } = sitePrompt({ page, sections, skin, fonts, identity: who });
      return { text: prompt, notes, missing };
    }
    const { brief, notes, missing } = siteBrief({ page, sections, skin, fonts, identity: who });
    return { text: JSON.stringify(brief, null, 2), notes, missing };
  };

  const copy = async (target) => {
    setMenuOpen(false);
    const { text, notes, missing } = build(target);
    try {
      await navigator.clipboard.writeText(text);
      setState({ target, ok: true, notes, missing });
    } catch (err) {
      /* Clipboard access can be refused, and "nothing happened" is the one
         outcome she must never be left with. */
      setState({ target, ok: false, notes, missing, error: err.message });
    }
  };

  const shown = state && COPY_TARGETS.find((t) => t.id === state.target);
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {ghl && (
          <button onClick={() => setPreviewOpen(true)} style={{ ...btnGhost, display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Eye size={14} /> Preview
          </button>
        )}
        {/* One target is not a choice, so it copies straight away. */}
        <div style={{ position: "relative", display: "inline-block" }}>
          <button onClick={() => (targets.length > 1 ? setMenuOpen(!menuOpen) : copy(targets[0].id))}
            aria-haspopup={targets.length > 1 ? "menu" : undefined} aria-expanded={targets.length > 1 ? menuOpen : undefined}
            style={{ ...btnGhost, display: "inline-flex", alignItems: "center", gap: 8 }}>
            <ClipboardCopy size={14} /> Copy spec
            {targets.length > 1 && <ChevronDown size={13} />}
          </button>
          {menuOpen && (
            <Menu2 head="Which builder?" items={targets.map((t) => [t.ico, t.name, t.hint, false, () => copy(t.id)])} />
          )}
        </div>
      </div>

      {previewOpen && (
        <SitePreview page={page} sections={sections}
          skin={brandSkin({ brand: identity.brand, voiceOutputs: outputs.voice || {} }).skin}
          identity={{ ...identity, ownerName: identity.ownerName || fromAccount }}
          onClose={() => setPreviewOpen(false)} />
      )}

      {state && (
        <div style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.6, color: B.mid, maxWidth: 620 }}>
          {state.ok
            ? <div style={{ color: B.teal, fontWeight: 600 }}>{shown.done}</div>
            : <div style={{ color: B.amber, fontWeight: 600 }}>Could not reach the clipboard — {state.error}</div>}

          {/* MISSING is what the builder needs and she has not written. It
              will fill these itself if she lets it. */}
          {state.missing.length > 0 && (
            <div style={{ marginTop: 8, color: B.amber }}>
              <b>It will make these up unless you add them:</b>
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                {state.missing.map((m) => <li key={m}>{m}</li>)}
              </ul>
            </div>
          )}

          {/* NOTES are things it did for her that she should know about — a
              colour nobody chose, a contrast that falls short, an audience it
              had to default. None of them stop the paste. */}
          {state.notes.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <b style={{ color: B.text }}>Before you paste:</b>
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                {state.notes.map((n) => <li key={n}>{n}</li>)}
              </ul>
            </div>
          )}

          <div style={{ marginTop: 8, color: B.faint }}>
            Tell the builder to use your words as they are and not to add testimonials, statistics or client names — it has none of yours, and it will invent them.
          </div>
        </div>
      )}
    </div>
  );
}
